/**
 * Animates a displayed number from its previous value to `target` — used by
 * the dashboard KPI cards so a refreshed count visibly settles rather than
 * snapping. Short (default 420ms), ease-out, one rAF loop, no dependency.
 *
 * Reduced motion: when the user asks for it, the value is returned as-is
 * with no animation at all (not merely a faster one). The DOM always ends
 * on the exact server value; the animation never shows a number the server
 * did not return as the final state.
 */
import { useEffect, useRef, useState } from 'react';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

export function useCountUp(target: number, durationMs = 420): number {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? target : 0));
  // What is on screen right now — the next animation starts from here. Kept
  // in a ref (not derived in cleanup) so StrictMode's mount/unmount/mount
  // double-invoke can never skip the animation or strand it on 0.
  const shownRef = useRef(value);

  useEffect(() => {
    const show = (n: number) => {
      shownRef.current = n;
      setValue(n);
    };
    if (prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
      show(target);
      return undefined;
    }
    const from = shownRef.current;
    if (from === target) return undefined;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      show(t < 1 ? Math.round(from + (target - from) * eased) : target);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);

  return value;
}
